import React, { useCallback, useEffect, useRef, useState } from 'react'
import { loadCV, findDocumentCorners, canvasFromImage, loadImage } from '../lib/cv'

const DETECT_INTERVAL_MS = 120 // how often we run edge detection
const DETECT_MAX_DIM = 480 // downscale frames for fast detection
const STABLE_FRAMES_NEEDED = 15 // consecutive stable detections before auto-capture
const STABLE_TOLERANCE = 0.012 // corner movement tolerance, fraction of frame diagonal
const CAPTURE_COOLDOWN_MS = 2500
// Minimum detection confidence (0..1, see findDocumentCorners) before the
// quad is shown at all. Detections that merely squeak past the internal
// quality gates hover around 0.25–0.45 — these are usually spurious fits on
// clutter when no document is in view, and constantly flashing a trapezoid
// at the user reads as noise. Genuine documents measure ~0.55 and above
// (0.70–1.0 in good conditions), so 0.5 hides the junk without costing
// real detections.
const MIN_CONFIDENCE = 0.5

/**
 * Gate auto-capture on quad quality so random junk (floor, walls, clutter)
 * doesn't trigger a capture. Manual capture is not affected.
 * - area must be a sensible fraction of the frame (not tiny, not the frame itself)
 * - opposite sides must have similar lengths (perspective-distorted rectangle)
 * - aspect ratio must be document-like
 * - at most one corner may hug the frame border (documents mostly in view)
 */
function isGoodCaptureQuad(corners, w, h) {
  if (!corners) return false
  // Shoelace area
  let area = 0
  for (let i = 0; i < 4; i++) {
    const a = corners[i]
    const b = corners[(i + 1) % 4]
    area += a.x * b.y - b.x * a.y
  }
  area = Math.abs(area) / 2
  const frameArea = w * h
  if (area < frameArea * 0.2 || area > frameArea * 0.9) return false

  const side = (i) => Math.hypot(corners[(i + 1) % 4].x - corners[i].x, corners[(i + 1) % 4].y - corners[i].y)
  const top = side(0)
  const right = side(1)
  const bottom = side(2)
  const left = side(3)
  if (Math.min(top, bottom) / Math.max(top, bottom) < 0.7) return false
  if (Math.min(left, right) / Math.max(left, right) < 0.7) return false

  const aspect = ((top + bottom) / 2) / ((left + right) / 2)
  if (aspect < 0.3 || aspect > 3.5) return false

  const margin = Math.min(w, h) * 0.02
  let edgeCorners = 0
  for (const p of corners) {
    if (p.x < margin || p.y < margin || p.x > w - margin || p.y > h - margin) edgeCorners++
  }
  return edgeCorners <= 1
}

export default function CameraScanner({ onPage, pageCount, lastThumb, onDone }) {
  const videoRef = useRef(null)
  const overlayRef = useRef(null)
  const detectCanvasRef = useRef(document.createElement('canvas'))
  const stateRef = useRef({ corners: null, stable: 0, lastCapture: 0, busy: false })
  const fileInputRef = useRef(null)

  const [cameraError, setCameraError] = useState(null)
  const [videoSize, setVideoSize] = useState(null)
  const [status, setStatus] = useState('Looking for document…')
  const [progress, setProgress] = useState(0)
  const [flash, setFlash] = useState(false)
  const [autoCapture, setAutoCapture] = useState(false)
  const autoCaptureRef = useRef(false)
  autoCaptureRef.current = autoCapture

  // Start camera
  useEffect(() => {
    let stream
    let cancelled = false
    ;(async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        const video = videoRef.current
        video.srcObject = stream
        await video.play()
        setVideoSize({ w: video.videoWidth, h: video.videoHeight })
      } catch (err) {
        setCameraError(err.message || String(err))
      }
    })()
    return () => {
      cancelled = true
      if (stream) stream.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const capture = useCallback((cornersInVideoCoords) => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const s = stateRef.current
    s.lastCapture = Date.now()
    s.stable = 0

    const full = document.createElement('canvas')
    full.width = video.videoWidth
    full.height = video.videoHeight
    full.getContext('2d').drawImage(video, 0, 0)

    setFlash(true)
    setTimeout(() => setFlash(false), 250)

    onPage({
      originalDataUrl: full.toDataURL('image/jpeg', 0.95),
      corners: cornersInVideoCoords,
    })
  }, [onPage])

  // Detection loop
  useEffect(() => {
    if (!videoSize) return
    let stopped = false
    let timer

    ;(async () => {
      const cv = await loadCV()
      if (stopped) return

      const tick = () => {
        if (stopped) return
        timer = setTimeout(tick, DETECT_INTERVAL_MS)
        const video = videoRef.current
        const overlay = overlayRef.current
        const s = stateRef.current
        if (!video || !overlay || video.readyState < 2 || s.busy) return

        s.busy = true
        try {
          const scale = DETECT_MAX_DIM / Math.max(video.videoWidth, video.videoHeight)
          const dc = detectCanvasRef.current
          dc.width = Math.round(video.videoWidth * scale)
          dc.height = Math.round(video.videoHeight * scale)
          dc.getContext('2d', { willReadFrequently: true }).drawImage(video, 0, 0, dc.width, dc.height)

          const src = cv.imread(dc)
          let corners = null
          try {
            corners = findDocumentCorners(cv, src)
          } finally {
            src.delete()
          }
          // Treat low-confidence detections as "no document": rendering a
          // trapezoid over random clutter erodes trust in the scanner.
          if (corners && (corners.confidence ?? 1) < MIN_CONFIDENCE) corners = null

          // Overlay is drawn in detection-canvas coordinates and CSS-stretched over the video
          overlay.width = dc.width
          overlay.height = dc.height
          const ctx = overlay.getContext('2d')
          ctx.clearRect(0, 0, overlay.width, overlay.height)

          const inCooldown = Date.now() - s.lastCapture < CAPTURE_COOLDOWN_MS

          if (corners) {
            drawQuad(ctx, corners)
            const diag = Math.hypot(dc.width, dc.height)
            const stable =
              s.corners &&
              corners.every((p, i) => Math.hypot(p.x - s.corners[i].x, p.y - s.corners[i].y) < diag * STABLE_TOLERANCE)
            const goodQuad = isGoodCaptureQuad(corners, dc.width, dc.height)
            // Only count frames toward auto-capture when the quad is both
            // stable AND high quality; any bad frame resets the lock.
            s.stable = stable && goodQuad ? s.stable + 1 : goodQuad ? 1 : 0
            s.corners = corners

            if (inCooldown) {
              setStatus('Captured ✓')
              setProgress(0)
            } else if (!autoCaptureRef.current) {
              setStatus('Document detected')
              setProgress(0)
            } else if (!goodQuad) {
              setStatus('Move closer to the document…')
              setProgress(0)
            } else {
              setStatus('Hold steady…')
              setProgress(Math.min(1, s.stable / STABLE_FRAMES_NEEDED))
              if (s.stable >= STABLE_FRAMES_NEEDED) {
                const toVideo = corners.map((p) => ({ x: p.x / scale, y: p.y / scale }))
                capture(toVideo)
              }
            }
          } else {
            s.corners = null
            s.stable = 0
            setProgress(0)
            setStatus(inCooldown ? 'Captured ✓' : 'Looking for document…')
          }
        } finally {
          s.busy = false
        }
      }
      tick()
    })()

    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [videoSize, capture])

  const manualCapture = () => {
    const s = stateRef.current
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    const scale = DETECT_MAX_DIM / Math.max(video.videoWidth, video.videoHeight)
    const corners = s.corners ? s.corners.map((p) => ({ x: p.x / scale, y: p.y / scale })) : null
    capture(corners)
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    const cv = await loadCV()
    for (const file of files) {
      const dataUrl = await new Promise((res) => {
        const r = new FileReader()
        r.onload = () => res(r.result)
        r.readAsDataURL(file)
      })
      const img = await loadImage(dataUrl)
      // Detect on a downscaled copy, then scale corners back up
      const scale = Math.min(1, DETECT_MAX_DIM / Math.max(img.width, img.height))
      const small = document.createElement('canvas')
      small.width = Math.round(img.width * scale)
      small.height = Math.round(img.height * scale)
      small.getContext('2d').drawImage(img, 0, 0, small.width, small.height)
      const src = cv.imread(small)
      let corners = null
      try {
        corners = findDocumentCorners(cv, src)
      } finally {
        src.delete()
      }
      onPage({
        originalDataUrl: canvasFromImage(img).toDataURL('image/jpeg', 0.95),
        corners: corners ? corners.map((p) => ({ x: p.x / scale, y: p.y / scale })) : null,
      })
    }
  }

  return (
    <div className="scanner">
      <div className="scanner-viewport">
        {cameraError ? (
          <div className="camera-error">
            <p>Camera unavailable: {cameraError}</p>
            <p>You can still add pages from image files.</p>
          </div>
        ) : (
          <div
            className="video-wrap"
            style={videoSize ? { aspectRatio: `${videoSize.w} / ${videoSize.h}` } : undefined}
          >
            <video ref={videoRef} playsInline muted />
            <canvas ref={overlayRef} className="overlay" />
            {flash && <div className="flash" />}
          </div>
        )}
      </div>

      <div className="status-bar">
        <span>{status}</span>
        {progress > 0 && (
          <div className="progress">
            <div className="progress-fill" style={{ width: `${progress * 100}%` }} />
          </div>
        )}
      </div>

      <div className="controls">
        <button className="btn" onClick={() => fileInputRef.current.click()} title="Add from image file">
          Upload
        </button>
        <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleUpload} />

        <button
          className="shutter"
          onClick={manualCapture}
          disabled={!!cameraError}
          aria-label="Capture"
          data-testid="capture-button"
        />

        <div className="mode-toggle" role="group" aria-label="Capture mode">
          <button
            className={'mode-option' + (!autoCapture ? ' mode-selected' : '')}
            onClick={() => setAutoCapture(false)}
            aria-pressed={!autoCapture}
          >
            Manual
          </button>
          <button
            className={'mode-option' + (autoCapture ? ' mode-selected' : '')}
            onClick={() => setAutoCapture(true)}
            aria-pressed={autoCapture}
          >
            Auto
          </button>
        </div>
      </div>

      <div className="bottom-bar">
        {lastThumb ? (
          <button className="thumb-btn" onClick={onDone}>
            <img src={lastThumb} alt="Last scan" />
            <span className="badge">{pageCount}</span>
          </button>
        ) : (
          <div className="thumb-placeholder" />
        )}
        <button className="btn btn-primary" onClick={onDone} disabled={pageCount === 0} data-testid="done-button">
          Done ({pageCount})
        </button>
      </div>
    </div>
  )
}

function drawQuad(ctx, corners) {
  ctx.beginPath()
  ctx.moveTo(corners[0].x, corners[0].y)
  for (let i = 1; i < 4; i++) ctx.lineTo(corners[i].x, corners[i].y)
  ctx.closePath()
  ctx.fillStyle = 'rgba(47, 128, 237, 0.25)'
  ctx.strokeStyle = 'rgba(47, 128, 237, 0.9)'
  ctx.lineWidth = 3
  ctx.fill()
  ctx.stroke()
  for (const p of corners) {
    ctx.beginPath()
    ctx.arc(p.x, p.y, 6, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(47, 128, 237, 0.9)'
    ctx.fill()
  }
}
